<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    :totalSections="1"
  />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="typeId"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="type"
    @cancel="$router.replace({ name: endPointData?.configurationPage })"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top', 'border-radius-bottom']"
      :sectionDescription="$t('configuration.type.details_description')"
      :sectionTitle="$t('configuration.type.details_title')"
    >
      <FormRow
        :errors="errors && errors[endPointData.payLoad]?.name"
        :label="$t('configuration.type.name_label')"
        labelFor="type_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors && errors[endPointData.payLoad]?.name }">
            <input
              id="type_name"
              v-model="type.name"
              :placeholder="$t('configuration.type.input_name')"
              @input="validateInput('type', 'name')"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="$t('configuration.type.description_label')"
        labelFor="type_description"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div class="input">
            <input
              id="type_description"
              v-model="type.description"
              :placeholder="$t('configuration.type.input_description')"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import { onMounted, reactive, ref, computed, provide } from 'vue';

// Utils
import { useOrganisation } from '@/functions/use_remote_object';
import { useRoute, useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';

// Components
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';

const createNewType = () => ({
  name: '',
  description: ''
});

const props = defineProps({
  organisationId: { type: String, required: true },
  typeId: { type: String, default: null },
});

const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const currentOrganisation = useOrganisation(props);
const loading = ref(false);
const initialType = ref(null);
const type = reactive(createNewType());
const errors = ref(null);
const saving = ref(false);

const imageValid = ref(true);

provide('currentOrganisation', currentOrganisation);

// All endpoints that this component uses
const endPointData = computed(() => {
  const list = [
    'equipmentTypeForm',
    'operationTypeForm'
  ];

  // Used includes to cover both 'newEquipmentTypeForm' and 'equipmentTypeForm'
  const name = list.find((listItem) => route.name.toLowerCase().includes(listItem.toLowerCase()));

  const data = {
    equipmentTypeForm: {
      types: `/organisations/${props.organisationId}/equipment_types`,
      type: `/organisations/${props.organisationId}/equipment_types/${props.typeId}`,
      payLoad: 'equipment_type',
      configurationPage: 'equipmentTypeConfiguration',
      get post_or_patch() {
        return props.typeId ? this.type : this.types;
      },
    },
    operationTypeForm: {
      types: `/organisations/${props.organisationId}/plant_types`,
      type: `/organisations/${props.organisationId}/plant_types/${props.typeId}`,
      payLoad: 'plant_type',
      configurationPage: 'operationTypeConfiguration',
      get post_or_patch() {
        return props.typeId ? this.type : this.types;
      },
    }
  };

  return data[name];
});

const loadType = async () => {
  if (props.typeId) {
    loading.value = true;
    let { ok, data } = await ajax(endPointData.value.type);

    if (ok) {
      initialType.value = data;
      Object.assign(type, data);
      loading.value = false;
    }
  } else {
    initialType.value = createNewType();
  }
};

onMounted(() => {
  loadType();
});

const formatTypeForPost = async () => {
  let typeParams = {
    name: type.name,
    description: type.description
  };

  return { [endPointData.value.payLoad]: typeParams };
};

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = props.typeId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(endPointData.value.post_or_patch, {
    method,
    body: await formatTypeForPost(),
  });

  if (ok) {
    router.replace({ name: endPointData.value.configurationPage });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

function validateInput(type, field) {
  if (errors.value && errors.value[type]) {
    errors.value[type][field] = null;
  }
}
</script>
